/*
 * Scroll-reveal — a calm fade-up as elements enter the viewport.
 *
 * Loaded as a plain <script defer> and wrapped in an IIFE so it can share a
 * page with the other defer scripts that declare a top-level `init` (e.g.
 * heroapp-faq.js / home-quiz-picker.js). Without isolation the second script
 * to parse throws "Identifier 'init' has already been declared".
 *
 * Markup contract:
 *   [data-reveal]            → element fades + rises into place on entry
 *   [data-reveal="fade"]     → fade only, no vertical shift
 *   [data-reveal-stagger]    → its direct [data-reveal] children cascade in
 *
 * The hidden start-state lives in snippets/theme-styles-variables.liquid,
 * gated entirely on the `.hh-reveal-on` class this script adds. We add that
 * class ONLY when motion is allowed AND IntersectionObserver exists — so a
 * no-JS, no-IntersectionObserver, or reduced-motion visitor never has any
 * content hidden from them. Tuned slow + soft to match the editorial voice.
 */
(() => {
  // Idempotent: many pages render several custom sections, each carrying its
  // own <script> include for section isolation. Run the setup only once.
  if (globalThis.__hhRevealInit) return;
  globalThis.__hhRevealInit = true;

  const STEP_MS = 90; // calm cascade between staggered siblings

  const motionOk =
    !globalThis.matchMedia ||
    !globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!motionOk || !('IntersectionObserver' in globalThis)) return;

  document.documentElement.classList.add('hh-reveal-on');

  // Hold the trigger above the viewport's bottom edge so the element has
  // scrolled well onto the screen before it animates — the motion reads as a
  // response to scrolling rather than firing while still off-screen. Desktop
  // viewports are taller, so they need a deeper offset than mobile.
  const desktop = globalThis.matchMedia('(min-width: 750px)');
  const bottomOffset = () => (desktop.matches ? 240 : 160);

  const onIntersect = (entries, obs) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-revealed');
      obs.unobserve(entry.target);
    });
  };

  const makeObserver = () =>
    new IntersectionObserver(onIntersect, {
      rootMargin: `0px 0px -${bottomOffset()}px 0px`,
      threshold: 0,
    });

  let observer = makeObserver();

  // Rebuild the observer when the viewport crosses the desktop breakpoint, so
  // elements not yet revealed adopt the new offset.
  desktop.addEventListener('change', () => {
    observer.disconnect();
    observer = makeObserver();
    document
      .querySelectorAll('[data-reveal]:not(.is-revealed)')
      .forEach((el) => observer.observe(el));
  });

  const init = () => {
    document.querySelectorAll('[data-reveal-stagger]').forEach((group) => {
      group.querySelectorAll(':scope > [data-reveal]').forEach((child, i) => {
        child.style.setProperty('--reveal-delay', `${i * STEP_MS}ms`);
      });
    });

    document.querySelectorAll('[data-reveal]').forEach((el) => {
      if (el.dataset.revealReady === 'true') return;
      el.dataset.revealReady = 'true';
      observer.observe(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-scan when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
